import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Banner from "../components/Banner";
import {
  IBannerData,
  IDownloadFormsData,
  INewsEventsData,
  IVacanciesData,
} from "../exports/interfaces";
import { dynamicFailed } from "../hooks/custom/useToast";
import { useLoader } from "../context/LoaderContext";
import { useMobile } from "../hooks/custom/useMobile";
import useTab from "../hooks/custom/useTab";
import {
  fetchAcademicVacancies,
  fetchDownloadForms,
  fetchVacancies,
} from "../api/VacanciesApi";
import { fetchNewsEvents } from "../api/HomeApi";
import { PublicNoticePost } from "../components/home/PublicNoticePost";
import { useTabs } from "../hooks/lib/useTabs";
import VacanciesSidebar from "../components/vacancies/VacanciesSidebar";
import VacanciesContent from "../components/vacancies/VacanciesContent";
import { fetchVacancyBanner } from "../api/BannerApi";

const VacanciesPage = () => {
  const { setLoading } = useLoader();
  const location = useLocation();
  const mobile = useMobile();
  const tab = useTab();
  const [openSidebar, setOpenSidebar] = useState(false);
  const [adminVacancies, setAdminVacancies] = useState<IVacanciesData[]>([]);
  const [academicVacancies, setAcademicVacancies] = useState<IVacanciesData[]>(
    [],
  );
  const [downloadForms, setDownloadForms] = useState<IDownloadFormsData[]>([]);
  const [vacancyBanner, setVacancyBanner] = useState<IBannerData[]>([]);
  const [newsEvents, setNewsEvents] = useState<INewsEventsData[]>([]);
  useTabs();

  const vacancies = [...adminVacancies, ...academicVacancies];
  const hasVacancyPosts = newsEvents.some(
    (post) => post.category === "Vacancy",
  );
  const positionWasRequested = ["#academic", "#admin"].includes(
    location.hash.toLowerCase(),
  );
  const showPositionInitially = !hasVacancyPosts || positionWasRequested;

  const [selectedVacancies, setSelectedVacancies] = useState<string>("");
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchVacancies(),
      fetchAcademicVacancies(),
      fetchDownloadForms(),
      fetchVacancyBanner(),
      fetchNewsEvents(),
    ])
      .then(
        ([
          vacanciesData,
          academicVacanciesData,
          downloadFormsData,
          vacancyBannerData,
          newsEventsData,
        ]) => {
          setAdminVacancies(vacanciesData);
          setAcademicVacancies(academicVacanciesData);
          setDownloadForms(downloadFormsData);
          setVacancyBanner(vacancyBannerData);
          setNewsEvents(newsEventsData);
          setLoading(false);
          window.location.hash.replace("#", "") === "admin"
            ? setSelectedVacancies(vacanciesData[0]?.adv_code)
            : setSelectedVacancies(academicVacanciesData[0]?.adv_code);
        },
      )
      .catch((error) => {
        dynamicFailed(error.response.data.error.message);
        setLoading(false);
      });
  }, [setLoading]);

  useEffect(() => {
    if (showPositionInitially) {
      document
        .querySelector<HTMLAnchorElement>('a[href="#vacancy_position"]')
        ?.click();
    }
  }, [showPositionInitially]);

  const selectedVacanciesDetails = vacancies.find(
    (item) => item.adv_code === selectedVacancies,
  );

  return (
    <>
      <Banner title="Academics" bannerImage={vacancyBanner?.[0]?.image?.[0]} />
      <section id="introduction">
        <div className="td_height_120 td_height_lg_40" />
        <div className="td_about td_style_1">
          <div className="container">
            <div className="td_tabs td_mb_40">
              <ul className="td_tab_links td_style_1 td_mp_0 td_fs_20 td_medium td_heading_color">
                <li className={showPositionInitially ? "" : "active"}>
                  <a
                    href="#vacancy_notice"
                    aria-disabled={!hasVacancyPosts}
                    style={
                      !hasVacancyPosts
                        ? { opacity: 0.45, pointerEvents: "none" }
                        : undefined
                    }
                  >
                    Notice
                  </a>
                </li>
                <li className={showPositionInitially ? "active" : ""}>
                  <a href="#vacancy_position">Vacancy Position</a>
                </li>
              </ul>
              <div className="td_height_30 td_height_lg_30" />
              <div className="td_tab_body">
                <div
                  className={`td_tab ${showPositionInitially ? "" : "active"}`}
                  id="vacancy_notice"
                >
                  <div className="row td_gap_y_30 justify-content-center">
                    {newsEvents
                      .filter((post) => post.category === "Vacancy")
                      .map((post) => (
                        <PublicNoticePost key={post.title} post={post} />
                      ))}
                  </div>
                </div>
                <div
                  className={`td_tab ${showPositionInitially ? "active" : ""}`}
                  id="vacancy_position"
                >
                  {(mobile || tab) && (
                    <div className="course_sidebar_toggle_wrapper td_mb_20">
                      <h3 className="mb-0">Vacancy Position</h3>
                      <button
                        className="course_sidebar_toggle mt-1"
                        onClick={() => setOpenSidebar(true)}
                      >
                        <span></span>
                        <span></span>
                        <span></span>
                        {/* <span></span>
                  <span></span>
                  <span></span> */}
                      </button>
                    </div>
                  )}
                  <div className="row td_gap_y_40 td_mb_40">
                    <VacanciesSidebar
                      adminVacancies={adminVacancies}
                      academicVacancies={academicVacancies}
                      selectedVacancies={selectedVacancies}
                      setSelectedVacancies={setSelectedVacancies}
                      openSidebar={openSidebar}
                      setOpenSidebar={setOpenSidebar}
                    />
                    <VacanciesContent
                      selectedVacanciesDetails={selectedVacanciesDetails}
                      downloadFormsData={downloadForms}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default VacanciesPage;
